import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  type GetVerktoyListeOperationResponse,
  type AddLaeringsaktivitetOperationResponse,
  type AddLaeringsaktivitetOperationRequest,
  type DeleteLaeringsaktivitetRequest,
  VERKTOY_PROXY_NAME,
  type VerktoyProxyErrorResponse,
  type GetVerktoyOperationResponse,
  type AvvisOppgaveResponse,
  type AvvisOppgaveRequest,
  type GetMineVerktoyListeOperationResponse,
  type GetMittVerktoyByGuidResponse,
} from '@src/types/apiTypes';
import { ApiURL } from '@store/constants/apiURLs';
import { getAPIRequestURLFromState } from '@utils/verktoyFilters';

import type { AppDispatch, VerktoyRootState } from '..';
import type { Verktoy } from '@src/types/minhelseentities';
import type { VerktoyFilterState } from '@src/types/stateTypes';

import { get, post, remove } from '@helsenorge/framework-utils/hn-proxy-service';

const NO_DATA_RETURNED = 'No data returned';

type FetchVerktoyListeThunkPayload = { isLoggedIn: boolean; verktoyFilters?: VerktoyFilterState };

export const fetchVerktoyListeThunk = createAsyncThunk<
  GetVerktoyListeOperationResponse,
  FetchVerktoyListeThunkPayload,
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: GetVerktoyListeOperationResponse;
    state: VerktoyRootState;
  }
>(
  'verktoy/fetchVerktoyListe',
  async ({ verktoyFilters, isLoggedIn }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const params = getAPIRequestURLFromState(isLoggedIn, verktoyFilters);
      // Siden hooks for Query benytter new URLSearchParams så vil vi få med + for space default.
      // Replacer for unngå å sende + som space til back end da virker BE støtter %20
      // I tillegg: Legger Query params direkte i URLen for å unngå at den skal bli dobbel encodet
      const queryString = params.toString().replace(/\+/g, '%20');
      const verktoyURLInnlogget = queryString ? `${ApiURL.VERKTOYLISTE}?${queryString}` : ApiURL.VERKTOYLISTE;
      const verktoyURLAnonym = queryString ? `${ApiURL.ANONYM_VERKTOYLISTE}?${queryString}` : ApiURL.ANONYM_VERKTOYLISTE;
      const response = isLoggedIn
        ? await get<GetVerktoyListeOperationResponse>(VERKTOY_PROXY_NAME, verktoyURLInnlogget)
        : await get<GetVerktoyListeOperationResponse>(VERKTOY_PROXY_NAME, verktoyURLAnonym);

      if (response === null) {
        return rejectWithValue(new Error(NO_DATA_RETURNED));
      }
      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().verktoy.verktoyListeLoading;
    },
  }
);

export const fetchMineVerktoyV2Thunk = createAsyncThunk<
  GetMineVerktoyListeOperationResponse,
  void,
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: GetMineVerktoyListeOperationResponse;
    state: VerktoyRootState;
  }
>(
  'verktoy/fetchLaeringsaktiviteter',
  async (_, { rejectWithValue, fulfillWithValue }) => {
    try {
      const data = await get<GetMineVerktoyListeOperationResponse>(VERKTOY_PROXY_NAME, ApiURL.MINE_VERKTOY_V2);
      if (data === null) {
        return rejectWithValue(new Error(NO_DATA_RETURNED));
      }
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().verktoy.laeringsaktiviteterLoading;
    },
  }
);

export const fetchMittVerktoyByGuidThunk = createAsyncThunk<
  GetMittVerktoyByGuidResponse,
  { laeringsressursGuid: string },
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: GetMittVerktoyByGuidResponse;
    state: VerktoyRootState;
  }
>(
  'verktoy/fetchMittVerktoyByGuid',
  async ({ laeringsressursGuid }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const data = await get<GetMittVerktoyByGuidResponse>(VERKTOY_PROXY_NAME, `${ApiURL.MINE_VERKTOY_V2}/${laeringsressursGuid}`);
      if (data === null) {
        return rejectWithValue(new Error(NO_DATA_RETURNED));
      }
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().verktoy.mittVerktoyByGuidLoading;
    },
  }
);

export const fetchVerktoyThunk = createAsyncThunk<
  GetVerktoyOperationResponse,
  { isLoggedIn: boolean; verktoyId: string; oppgaveId?: string },
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: GetVerktoyListeOperationResponse;
    state: VerktoyRootState;
  }
>(
  'verktoy/fetchVerktoy',
  async ({ verktoyId, isLoggedIn }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = isLoggedIn
        ? await get<GetVerktoyOperationResponse>(VERKTOY_PROXY_NAME, ApiURL.VERKTOY, {
            VerktoyId: verktoyId,
            IncludeLaeringsaktivitet: true,
          })
        : await get<GetVerktoyOperationResponse>(VERKTOY_PROXY_NAME, ApiURL.ANONYM_VERKTOY, {
            VerktoyId: verktoyId,
            IncludeLaeringsaktivitet: true,
          });

      if (response === null) {
        return rejectWithValue(new Error(NO_DATA_RETURNED));
      }
      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: ({ verktoyId }, { getState }) => {
      return !getState().verktoy.verktoyLoading && !!verktoyId;
    },
  }
);

type AddLaeringsaktivitetThunkPayload = { verktoy: Verktoy; oppgaveId?: string };

export const addLaeringsaktivitetThunk = createAsyncThunk<
  AddLaeringsaktivitetOperationResponse,
  AddLaeringsaktivitetThunkPayload,
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: AddLaeringsaktivitetOperationResponse;
    state: VerktoyRootState;
  }
>(
  'verktoy/addLaeringsaktivitet',
  async ({ verktoy, oppgaveId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      const response = await post<AddLaeringsaktivitetOperationResponse, AddLaeringsaktivitetOperationRequest>(
        VERKTOY_PROXY_NAME,
        ApiURL.VERKTOY,
        {
          VerktoyId: verktoy.VerktoyId,
          OppgaveId: oppgaveId,
        }
      );
      if (response === null) {
        return rejectWithValue(new Error(NO_DATA_RETURNED));
      }
      const updatedVerktoy: AddLaeringsaktivitetOperationResponse = {
        ...verktoy,
        Laeringsaktivitet: response.Laeringsaktivitet,
      };
      return fulfillWithValue(updatedVerktoy);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: ({ verktoy }, { getState }) => {
      return !getState().verktoy.isWaitingForAktivitetResponse && !!verktoy.VerktoyId;
    },
  }
);

type AvvisOppgavePayload = { verktoy: Verktoy; oppgaveId: string };

export const avvisOppgaveLaeringsaktivitetThunk = createAsyncThunk<
  null,
  AvvisOppgavePayload,
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: void;
  }
>(
  'verktoy/avvisOppgaveLaeringsaktivitet',
  async ({ verktoy, oppgaveId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      await post<AvvisOppgaveResponse, AvvisOppgaveRequest>(VERKTOY_PROXY_NAME, ApiURL.AVVIS_OPPGAVE, {
        VerktoyId: verktoy.VerktoyId,
        OppgaveId: oppgaveId,
      });
      return fulfillWithValue(null);
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: ({ verktoy }) => {
      return !!verktoy.VerktoyId;
    },
  }
);

type RemoveLaeringsaktivitetPayload = { aktivitetsId: number };
type RemoveLaeringsaktivitetResponse = { aktivitetsId: number };

export const removeLaeringsaktivitetThunk = createAsyncThunk<
  RemoveLaeringsaktivitetResponse,
  RemoveLaeringsaktivitetPayload,
  {
    dispatch: AppDispatch;
    rejectValue: VerktoyProxyErrorResponse | Error;
    fulfillWithValue: void;
    state: VerktoyRootState;
  }
>(
  'verktoy/removeLaeringsaktivitet',
  async ({ aktivitetsId }, { rejectWithValue, fulfillWithValue }) => {
    try {
      await remove<object, DeleteLaeringsaktivitetRequest>(VERKTOY_PROXY_NAME, ApiURL.VERKTOY, {
        LaeringsaktivitetId: aktivitetsId,
      });
      return fulfillWithValue({ aktivitetsId });
    } catch (error) {
      return rejectWithValue(error as VerktoyProxyErrorResponse);
    }
  },
  {
    condition: ({ aktivitetsId }, { getState }) => {
      return !!aktivitetsId && !getState().verktoy.isWaitingForAktivitetResponse;
    },
  }
);
